import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { displayNameFor, shouldRenderHandlePill, handleFor } from '@/lib/displayName';
import MediaAnnotator from '@/components/MediaAnnotator';
import ModelViewer3D from '@/components/ModelViewer3D';
import Asset3DReviewSplit from '@/components/Asset3DReviewSplit';
import AnnotationThumbnail from '@/components/AnnotationThumbnail';
import AssetAuditTrailModal from '@/components/AssetAuditTrailModal';
import LineupPlayer from '@/components/LineupPlayer';
import LineupReviewPanel from '@/components/LineupReviewPanel';
import CompareView from '@/components/CompareView';
import { ArrowLeft, CheckCircle2, RotateCcw, XCircle, MessageSquare, Clock, Film, Image as ImageIcon, ChevronRight, ChevronDown, Filter, User, Sparkles, Inbox, Send, ShieldCheck, Trash2, Activity, Music, Box } from 'lucide-react';

/* 11-department taxonomy (mirrors DepartmentGantt). Every submission.stage
 * gets mapped into one of these buckets so the client sees their approvals
 * grouped by production phase. */
const DEPTS = [
  { key: 'script',     label: 'Script',                 color: '#60a5fa', match: ['script'] },
  { key: 'storyboard', label: 'Storyboard / Animatics', color: '#a78bfa', match: ['storyboard','board','animatic'] },
  { key: 'concept',    label: 'Concept Art',            color: '#f472b6', match: ['concept','design'] },
  { key: 'modeling',   label: 'Asset Build',            color: '#10b981', match: ['model','modeling','textur','shade','shading','asset_build'] },
  { key: 'rigging',    label: 'Rigging',                color: '#14b8a6', match: ['rig'] },
  { key: 'animation',  label: 'Layout & Animation',     color: '#f59e0b', match: ['anim','animation','layout','fx'] },
  { key: 'lighting',   label: 'Lighting',               color: '#fb923c', match: ['light','lighting'] },
  { key: 'rendering',  label: 'Rendering',              color: '#ec4899', match: ['render','rendering'] },
  { key: 'comp',       label: 'Comp & Edit',            color: '#84cc16', match: ['comp','edit','editing','composit'] },
  { key: 'sound',      label: 'Sound & Music',          color: '#eab308', match: ['sound','music','audio','vo'] },
  { key: 'delivery',   label: 'Final Delivery',         color: '#ef4444', match: ['deliver','final','master'] },
];
function deptOf(stage) {
  const s = (stage || '').toLowerCase();
  return DEPTS.find(d => d.match.some(m => s.includes(m))) || null;
}

/** Client Review Page
 *  =================
 *  Route: /reviews  → inbox of submissions awaiting client approval
 *  Route: /reviews/:id → single submission with annotation canvas + decision bar
 *
 *  This page is also reachable for Supervisors (internal inbox) via ?mode=internal
 */
export default function ClientReviewPage() {
  const { api, user } = useAuth();
  const { id: paramId } = useParams();
  const navigate = useNavigate();
  const [mode, setMode] = useState('client'); // supervisor | pm | client
  const [inbox, setInbox] = useState([]);
  const [selectedId, setSelectedId] = useState(paramId || null);
  const [detail, setDetail] = useState(null);
  const [mediaIdx, setMediaIdx] = useState(0);
  const [mediaUrls, setMediaUrls] = useState({}); // idx -> blob url
  const [decisionNote, setDecisionNote] = useState('');
  const [filter, setFilter] = useState('open'); // all | open | resolved
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  // Founder ask May 25, 2026: Approvals page should land on the new
  // Layout & Animation CompareView by default (NOT the legacy tile
  // grid that shows for "All Departments"). Every shot the artists
  // submit via MY TASKS lives on Layout/Animation today — so default
  // the dept selection to 'animation' and let users opt-in to other
  // depts / "All" by clicking the sidebar rail.
  const [selectedDept, setSelectedDept] = useState('animation');
  // ── Dept tree expansion (May 20 2026) ──
  // Founder spec: clicking a dept like "Layout & Animation" expands a
  // children list showing every artist who submitted to that dept (plus
  // a "Full Lineup" entry at the top). Internal artists are listed first,
  // then vendors. Click an artist → filter grid to ONLY their shots in
  // sequence. Click Full Lineup → sequential autoplay of all submissions.
  const [expandedDept, setExpandedDept] = useState(null);
  const [selectedArtist, setSelectedArtist] = useState(null); // {dept_key, submitter_id, name}
  const [lineupDept, setLineupDept] = useState(null); // dept_key when lineup view is active
  // Founder ask Jun 24 2026: Layout & Animation tab needs a toggle so
  // clients can land on the full episode lineup (single-window player +
  // annotations + frame-by-frame, like the legacy Animatics review) but
  // internal roles can still flip to the SyncSketch shot-by-shot
  // CompareView. Default = 'lineup' for clients, 'compare' for internal.
  // `viewAsClient=true` lets an admin/PM preview exactly what the
  // client sees right now.
  const isClientRole = (user?.role === 'client' || user?.role === 'client_producer');
  const [animationView, setAnimationView] = useState(isClientRole ? 'lineup' : 'compare');
  const [viewAsClient, setViewAsClient] = useState(false);
  const [historyView, setHistoryView] = useState('pending'); // pending | approved | revise | rejected | all
  const [auditTrailAssetId, setAuditTrailAssetId] = useState(null);
  const [projectFilter, setProjectFilter] = useState('all'); // 'all' | project_id
  const [overrideOpen, setOverrideOpen] = useState(false);
  // Founder ask Feb 16, 2026: live count per gate (supervisor/pm/client/feedback)
  // so the user can see at a glance which gate has work pending BEFORE clicking.
  // Without this, the empty "All caught up" state on each gate makes users
  // think the gate is broken when it just legitimately has no items.
  const [gateCounts, setGateCounts] = useState({ supervisor: 0, pm: 0, client: 0, feedback: 0 });
  // Founder ask Jun 25 2026 — Session 255. In Client Gate's Shot-by-shot
  // tab, the dropdown was empty (or showing the lineup as if it were a
  // shot) because the client inbox only contains `status=client_review`
  // rows, which is just the lineup. The supplement pulls per-shot
  // animator playblasts that have passed Supervisor approval so the
  // client can drill into individual shots side-by-side with the CBA
  // reference — even though formal client approval still happens on
  // the LINEUP in the adjacent tab.
  const [clientShotSupplement, setClientShotSupplement] = useState([]);
  const [clientSupplementLoading, setClientSupplementLoading] = useState(false);
  // Pin ↔ note sync — clicking a pin on the video, a marker on the timeline,
  // or a row in the right pane sets this id. Both halves of the UI react:
  // the right pane scrolls + highlights the matching note, and the video
  // seeks to the note's timecode.
  const [activeAnnId, setActiveAnnId] = useState(null);
  // Increments on every "seek again" intent (clicking the same row twice
  // should still re-seek the video). MediaAnnotator listens for changes to
  // this value to force its seek effect to re-run.
  const [seekTick, setSeekTick] = useState(0);
  const activateAnn = useCallback((id) => {
    setActiveAnnId(id);
    setSeekTick(t => t + 1);
  }, []);

  // 3D model detection helpers
  // Founder ask Feb 2026: .blend is now previewable thanks to the VPS render
  // service that auto-generates OBJ + GLB proxies. ModelViewer3D will detect
  // .blend and transparently swap in the proxy GLB.
  const MODEL_EXTS = /\.(glb|gltf|fbx|obj|blend)$/i;
  // Source files that the modeller authored but the browser can't render
  // (e.g. .blend is a binary Blender format, .ma/.mb are Maya scenes). These
  // should never be auto-selected — the browser viewer is blank for them.
  const SOURCE_EXTS = /\.(blend|ma|mb|max|c4d|hip|psd|ai|usd|usda|usdz|zip|7z|rar)$/i;
  const is3DModel = (media) => {
    if (!media) return false;
    const fn = media.filename || media.storage_key || '';
    return MODEL_EXTS.test(fn) || media.type === '3d' || media.type === 'model';
  };
  const get3DFormat = (media) => {
    const fn = media?.filename || media?.storage_key || '';
    if (/\.glb$/i.test(fn)) return 'glb';
    if (/\.gltf$/i.test(fn)) return 'gltf';
    if (/\.fbx$/i.test(fn)) return 'fbx';
    if (/\.obj$/i.test(fn)) return 'obj';
    if (/\.blend$/i.test(fn)) return 'blend'; // ModelViewer3D upgrades to proxy
    return 'glb';
  };
  /** Founder spec May 13, 2026: when a submission carries both a source
   * file (.blend / .ma / .mb / .psd) AND a previewable export (.glb /
   * .mp4 / .png), pick the FIRST previewable one by default. Otherwise
   * the viewer renders blank and the reviewer thinks the asset is
   * broken — when actually they're just looking at a binary source. */
  const isSourceFile = (media) => {
    const fn = media?.filename || media?.storage_key || '';
    return SOURCE_EXTS.test(fn);
  };
  const isPreviewable = (media) => {
    if (!media || isSourceFile(media)) return false;
    if (is3DModel(media)) return true;
    const t = (media.type || media.ftype || '').toLowerCase();
    if (['image', 'video', 'audio'].includes(t)) return true;
    const fn = (media.filename || media.storage_key || '').toLowerCase();
    return /\.(jpg|jpeg|png|webp|gif|bmp|svg|mp4|webm|mov|m4v|mp3|wav|ogg|flac|aac)$/i.test(fn);
  };
  const pickInitialMediaIdx = (media) => {
    if (!Array.isArray(media) || media.length === 0) return 0;
    const idx = media.findIndex(isPreviewable);
    return idx >= 0 ? idx : 0;
  };

  const [projectList, setProjectList] = useState([]); // {id, name, is_show, parent_project_id, episode_number}

  // Founder bug fix Feb 2026: cg_supervisor (Jaykrushna), vfx_supervisor,
  // art_director, animation_director, director, technical_director,
  // executive_producer, line_producer, studio_director also act as
  // Supervisor-Gate reviewers — mirror the backend STUDIO_REVIEWERS set
  // (notifications.CREATIVE_LEADERSHIP_ROLES + admin/PM). Without this,
  // a CG Supervisor logging in saw zero supervisor-gate items.
  const isSupervisor = [
    'master_admin', 'production_manager',
    'supervisor', 'creative_director', 'studio_director',
    'director', 'art_director', 'animation_director',
    'vfx_supervisor', 'cg_supervisor', 'technical_director',
    'executive_producer', 'line_producer',
  ].includes(user?.role);
  const isPM = ['master_admin','production_manager'].includes(user?.role);
  const isCreativeDirector = user?.role === 'creative_director';
  // PM view = view-only + forward-to-client. No annotation tools.
  const pmOnly = user?.role === 'production_manager';
  const isClient = ['client_producer','client_director'].includes(user?.role);

  // Decide default mode based on the OPENED submission's current gate first
  // (so master_admins/PMs see the right action button for whatever gate the
  // sub is at). Falls back to a role-default when nothing is open. Admins can
  // override via the mode tabs at any time.
  useEffect(() => {
    const subStatus = detail?.submission?.status;
    if (subStatus === 'supervisor_review' && isSupervisor && !isClient) { setMode('supervisor'); return; }
    if (subStatus === 'pm_review' && isPM && !isClient) { setMode('pm'); return; }
    if (subStatus === 'client_review') { setMode('client'); return; }
    // No submission open yet — default by role
    if (isPM && !isClient) setMode('pm');
    else if (isSupervisor && !isClient) setMode('supervisor');
    else setMode('client');
  }, [isSupervisor, isPM, isClient, detail?.submission?.status]);

  // Keep URL and selectedId in sync
  useEffect(() => { setSelectedId(paramId || null); }, [paramId]);
  const openSubmission = (id) => navigate(`/reviews/${id}`);
  const backToInbox = () => navigate('/reviews');

  // Load inbox
  const loadInbox = async (modeVal = mode, viewVal = historyView) => {
    try {
      const endpoint = modeVal === 'supervisor'
        ? '/reviews/pending-internal'
        : modeVal === 'pm'
          ? '/reviews/pending-pm'
          : modeVal === 'feedback'
            ? '/reviews/client-feedback'
            : '/reviews/pending-client';
      // Only the client inbox supports the history-view filter
      const params = (modeVal === 'client') ? { view: viewVal } : {};
      const { data } = await api.get(endpoint, { params });
      setInbox(data.items || []);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { loadInbox(mode, historyView); }, [mode, historyView]);

  // Move approved item back to pending review (within 48hr window)
  const moveToPending = async (subId) => {
    if (!window.confirm('Move this back to Pending Review? Within 48hrs = no deadline impact. After 48hrs = may impact deadlines.')) return;
    setBusy(true);
    try {
      await api.put(`/reviews/${subId}/revert-to-pending`);
      setMsg('Moved back to Pending Review');
      loadInbox(mode, historyView);
      if (selectedId === subId) { setDetail(null); setSelectedId(null); navigate('/reviews'); }
    } catch (e) {
      setMsg(e.response?.data?.detail || 'Failed to revert');
    }
    setBusy(false);
    setTimeout(() => setMsg(''), 3000);
  };

  // Load the user's accessible projects for the picker dropdown
  useEffect(() => {
    api.get('/projects').then(r => setProjectList(r.data || [])).catch(() => setProjectList([]));
  }, []);

  // Founder ask Feb 16, 2026: live counts per gate. Re-fetch when:
  //   • the project filter changes (so badges reflect the scoped view)
  //   • the user changes mode (a decision likely just decreased a count)
  //   • the inbox reloads (new submission arrived via realtime)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const params = projectFilter !== 'all' ? { project_id: projectFilter } : {};
        const { data } = await api.get('/reviews/pending-count', { params });
        if (!cancelled) setGateCounts({
          supervisor: data.supervisor || 0,
          pm:         data.pm || 0,
          client:     data.client || 0,
          feedback:   data.client_feedback || 0,
        });
      } catch { /* network blip — keep last counts */ }
    })();
    return () => { cancelled = true; };
  }, [projectFilter, mode, inbox.length]);

  // Load detail when selected
  useEffect(() => {
    if (!selectedId) { setDetail(null); return; }
    let cancelled = false;
    (async () => {
      const { data } = await api.get(`/reviews/${selectedId}`);
      if (cancelled) return;
      setDetail(data);
      // Auto-select the first previewable media so reviewers don't land
      // on a blank .blend / source-file tab by default. Founder ask May 13.
      setMediaIdx(pickInitialMediaIdx(data?.submission?.media));
      setMediaUrls({});
    })();
    return () => { cancelled = true; };
  }, [selectedId]);

  // Founder spec May 12, 2026: Sketchfab-style asset review wants the
  // last approved concept/reference art alongside the 3D model. We only
  // fetch this when a 3D submission is selected — keeps the inbox light.
  const [reference, setReference] = useState(null);
  const reloadReference = useCallback(async () => {
    if (!selectedId) { setReference(null); return; }
    try {
      const { data } = await api.get(`/reviews/${selectedId}/reference`);
      setReference(data && data.cloud_url ? data : null);
    } catch {
      setReference(null);
    }
  }, [api, selectedId]);
  useEffect(() => { reloadReference(); }, [reloadReference]);

  // Fetch media blobs (authenticated) with fallback to direct url
  useEffect(() => {
    const sub = detail?.submission;
    if (!sub) return;
    const m = sub.media?.[mediaIdx];
    if (!m) return;
    if (mediaUrls[mediaIdx]) return;
    (async () => {
      try {
        const key = m.storage_key || m.key;
        if (key) {
          const resp = await api.get(`/reviews/media?key=${encodeURIComponent(key)}`, { responseType: 'blob' });
          if (resp.data && resp.data.size > 0) {
            const blobUrl = URL.createObjectURL(resp.data);
            setMediaUrls(prev => ({ ...prev, [mediaIdx]: blobUrl }));
            return;
          }
        }
      } catch {}
      // Fallback: use the direct url field if available
      if (m.url) {
        const directUrl = m.url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${m.url}` : m.url.startsWith('http') ? m.url : `${process.env.REACT_APP_BACKEND_URL}/api${m.url}`;
        setMediaUrls(prev => ({ ...prev, [mediaIdx]: directUrl }));
      } else if (m.storage_key) {
        // Last resort: construct URL from storage key
        setMediaUrls(prev => ({ ...prev, [mediaIdx]: `${process.env.REACT_APP_BACKEND_URL}/api/reviews/media?key=${encodeURIComponent(m.storage_key)}` }));
      }
    })();
  }, [detail, mediaIdx]);

  // Cleanup blob URLs
  useEffect(() => () => { Object.values(mediaUrls).forEach(u => URL.revokeObjectURL(u)); }, []);

  const annotations = useMemo(() => {
    if (!detail) return [];
    const all = (detail.annotations || []).filter(a => a.media_index === mediaIdx);
    if (filter === 'open') return all.filter(a => !a.resolved);
    if (filter === 'resolved') return all.filter(a => a.resolved);
    return all;
  }, [detail, mediaIdx, filter]);

  // Group inbox by department (top-level hook — must be before any return).
  // First filters by selected project (or parent show — episodes inherit show).
  const filteredInbox = useMemo(() => {
    if (projectFilter === 'all') return inbox;
    return inbox.filter(s => {
      // Match either the submission's direct project_id OR its parent show
      // (so picking "FairyTales (Show)" surfaces submissions from all 3 episodes).
      return s.project_id === projectFilter || s.parent_show_id === projectFilter;
    });
  }, [inbox, projectFilter]);

  // Resolve a media object's playable blob URL — auth-aware, same logic
  // as the detail-view loader but reusable from the LineupPlayer. Returns
  // a blob URL when the auth'd fetch succeeds; falls back to the public
  // /api/reviews/media URL otherwise (this fallback still requires the
  // cookie/session on production but works when the user has a session).
  const resolveMediaUrl = useCallback(async (m) => {
    if (!m) return '';
    try {
      // Founder fix (Feb 7 2026): when the media payload only carries a
      // `url` shaped like `/api/reviews/media?key=vos/cba/...`, extract
      // the storage_key inline so the auth-aware blob fetch runs. The
      // browser's <video> tag does NOT send our JWT bearer header on
      // direct GETs, so falling through to the raw URL gets a silent
      // 401 and the player stays stuck at "Loading..." forever.
      //
      // Founder fix (Feb 8 2026): same problem also affects the
      // /api/reviews/cba-stream/<clip_id> lazy materializer — it
      // requires auth and returns a 307 redirect to the cached media
      // URL. Both need to go through the authenticated blob fetch.
      let key = m.storage_key || m.key;
      if (!key && typeof m.url === 'string' && m.url.startsWith('/api/reviews/media?key=')) {
        try { key = decodeURIComponent(m.url.split('key=')[1].split('&')[0]); } catch (_e) { /* noop */ }
      }
      if (key) {
        const resp = await api.get(`/reviews/media?key=${encodeURIComponent(key)}`, { responseType: 'blob' });
        if (resp?.data && resp.data.size > 0) return URL.createObjectURL(resp.data);
      }
      // Lazy-materializer path: /api/reviews/cba-stream/<clip_id>
      // axios will follow the 307 redirect automatically and stream
      // the bytes through the authenticated channel.
      if (typeof m.url === 'string' && m.url.startsWith('/api/reviews/cba-stream/')) {
        const rel = m.url.replace(/^\/api/, '');
        const resp = await api.get(rel, { responseType: 'blob' });
        if (resp?.data && resp.data.size > 0) return URL.createObjectURL(resp.data);
      }
    } catch (_e) { /* fall through */ }
    if (m.url) {
      return m.url.startsWith('/api')
        ? `${process.env.REACT_APP_BACKEND_URL}${m.url}`
        : (m.url.startsWith('http') ? m.url : `${process.env.REACT_APP_BACKEND_URL}/api${m.url}`);
    }
    if (m.storage_key) return `${process.env.REACT_APP_BACKEND_URL}/api/reviews/media?key=${encodeURIComponent(m.storage_key)}`;
    return '';
  }, [api]);

  const groupedByDept = useMemo(() => {
    const g = {};
    DEPTS.forEach(d => { g[d.key] = []; });
    g.__unmatched__ = [];
    filteredInbox.forEach(sub => {
      const d = deptOf(sub.stage);
      if (d) g[d.key].push(sub);
      else g.__unmatched__.push(sub);
    });
    return g;
  }, [filteredInbox]);

  // ─── Client Gate Shot-by-shot supplement (Session 255, Jun 25 2026) ─
  // When the client opens the Shot-by-shot tab inside Client Gate, the
  // existing inbox only contains the LINEUP (status=client_review) and
  // therefore the per-shot dropdown is empty. We supplement that view
  // with per-shot animation submissions that have passed Supervisor
  // approval (`supervisor_decision == "approved"`) so the client can
  // inspect individual shots side-by-side with the CBA reference.
  // Formal client approval still happens on the LINEUP in the other tab.
  useEffect(() => {
    // Bail out unless we're in the exact view that needs the supplement.
    if (mode !== 'client') { setClientShotSupplement([]); return; }
    if (selectedDept !== 'animation') return;
    if (animationView !== 'compare') return;
    // Pick a project to scope the query to. Prefer the explicit project
    // filter; otherwise infer from the first lineup in the inbox.
    const anim = groupedByDept.animation || [];
    const lineup = anim.find(s => (s.kind === 'episode_lineup'));
    let pid = '';
    if (projectFilter && projectFilter !== 'all') pid = projectFilter;
    else if (lineup) pid = lineup.project_id;
    else if (anim[0]) pid = anim[0].project_id;
    if (!pid) { setClientShotSupplement([]); return; }
    const chapter = lineup?.chapter_name || '';
    let abort = false;
    (async () => {
      setClientSupplementLoading(true);
      try {
        const params = new URLSearchParams({
          project_id: pid,
          stage: 'animation',
        });
        if (chapter) params.set('chapter_name', chapter);
        const r = await api.get(`/reviews/client-shot-supplement?${params.toString()}`);
        if (!abort) setClientShotSupplement(r.data?.items || []);
      } catch (_e) {
        if (!abort) setClientShotSupplement([]);
      } finally {
        if (!abort) setClientSupplementLoading(false);
      }
    })();
    return () => { abort = true; };
    // We deliberately depend on `groupedByDept.animation` so the
    // supplement refreshes when the inbox reloads after a redeploy
    // or a manual refresh.
  }, [mode, selectedDept, animationView, projectFilter, groupedByDept.animation, api]);

  // Merged submissions for the Client Gate Shot-by-shot CompareView.
  // Filters lineups out (they belong on the adjacent Lineup tab) and
  // appends the supervisor-approved per-shot submissions.
  const compareSubmissionsForClientGate = useMemo(() => {
    if (mode !== 'client' || selectedDept !== 'animation' || animationView !== 'compare') {
      return groupedByDept.animation || [];
    }
    const anim = groupedByDept.animation || [];
    const nonLineup = anim.filter(s => s.kind !== 'episode_lineup');
    // De-dupe by submission id — supplement may already include rows
    // that the client inbox surfaced (e.g. if the supervisor approved
    // AND the PM forwarded to client in the same project).
    const seen = new Set(nonLineup.map(s => s.id));
    const merged = [...nonLineup];
    (clientShotSupplement || []).forEach(s => {
      if (!seen.has(s.id)) { merged.push(s); seen.add(s.id); }
    });
    return merged;
  }, [mode, selectedDept, animationView, groupedByDept.animation, clientShotSupplement]);

  // ─── ARTISTS-PER-DEPT (May 20 2026) ─────────────────────────────
  // Founder spec: clicking a dept like "Layout & Animation" expands a
  // children list showing every artist who submitted to that dept (plus
  // a "Full Lineup" entry at the top). Internal artists first, then
  // vendors. Click an artist → grid filters to ONLY their shots in
  // sequence. Click Full Lineup → sequential autoplay.
  const VENDOR_ROLES = new Set([
    'vendor_producer', 'vendor_artist', 'freelance_animator',
    'freelance_layout_artist', 'freelance_modeler', 'freelance_rigger',
    'freelance_lighter', 'freelance_fx_artist', 'freelance_compositor',
    'freelance_editor', 'freelance_concept_artist', 'freelance',
  ]);
  const isVendorRole = (r) => VENDOR_ROLES.has((r || '').toLowerCase());

  const artistsByDept = useMemo(() => {
    const out = {};
    DEPTS.forEach(d => { out[d.key] = []; });
    Object.entries(groupedByDept).forEach(([deptKey, subs]) => {
      if (deptKey === '__unmatched__' || !Array.isArray(subs) || subs.length === 0) return;
      const byArtist = new Map();
      subs.forEach(s => {
        const id = s.submitter_id || s.created_by || 'unknown';
        if (!byArtist.has(id)) {
          byArtist.set(id, {
            submitter_id: id,
            name: s.submitter_name || s.submitter_handle || 'Unknown',
            handle: s.submitter_handle || '',
            role: s.submitter_role || '',
            count: 0,
          });
        }
        byArtist.get(id).count += 1;
      });
      const list = Array.from(byArtist.values());
      // Internal artists first (alphabetical), then vendors (alphabetical).
      list.sort((a, b) => {
        const av = isVendorRole(a.role), bv = isVendorRole(b.role);
        if (av !== bv) return av ? 1 : -1;
        return (a.name || '').localeCompare(b.name || '');
      });
      out[deptKey] = list;
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupedByDept]);

  // Session 255 — same idea as `compareSubmissionsForClientGate`. The
  // CompareView's Artist dropdown is fed from `artistsByDept`; for the
  // Client Gate Shot-by-shot case we need to surface the per-shot
  // artists from the supplement too (otherwise the dropdown lists
  // only the producer who uploaded the lineup, which is wrong).
  const artistsByDeptForCompare = useMemo(() => {
    if (mode !== 'client' || selectedDept !== 'animation' || animationView !== 'compare') {
      return artistsByDept;
    }
    if (!clientShotSupplement.length) return artistsByDept;
    // Clone and extend the animation list with supplement artists.
    const clone = { ...artistsByDept };
    const seen = new Map();
    (clone.animation || []).forEach(a => seen.set(a.submitter_id, { ...a }));
    clientShotSupplement.forEach(s => {
      const id = s.submitter_id || s.created_by || 'unknown';
      if (!seen.has(id)) {
        seen.set(id, {
          submitter_id: id,
          name: s.submitter_name || s.submitter_handle || 'Unknown',
          handle: s.submitter_handle || '',
          role: s.submitter_role || '',
          count: 0,
        });
      }
      seen.get(id).count += 1;
    });
    const list = Array.from(seen.values()).sort((a, b) => {
      const av = isVendorRole(a.role), bv = isVendorRole(b.role);
      if (av !== bv) return av ? 1 : -1;
      return (a.name || '').localeCompare(b.name || '');
    });
    return { ...clone, animation: list };
  }, [artistsByDept, clientShotSupplement, mode, selectedDept, animationView]);

  // Extract shot sequence number from a submission for shot-order sorting.
  const shotSeqOf = (s) => {
    const src = String(s.parent_code || s.title || '');
    const m = src.match(/SH[_-]?(\d+)/i);
    if (m) return parseInt(m[1], 10);
    return 9999;
  };

  // ─── CLUBBED-UPLOAD SPREADER (May 20 2026) ──────────────────────────
  // Founder spec: "WHy these shots are still clubbed, please spread them
  // as individual shots, and rename them as per our NOMENCLATURE."
  //
  // When an artist is selected in a dept, explode every multi-file
  // submission into VIRTUAL per-file cards in the grid. Each virtual
  // card carries a nomenclature-style derived label like
  // `SH_006 · LAY · v001` extracted from the file's actual name.
  // Parent submission id is preserved so approve/reject/comment still
  // act on the real underlying record — the spreader is display-only.
  const FILE_TASK_CODES = {
    LAY: 'LAY', LAYOUT: 'LAY',
    ANM: 'ANM', ANIM: 'ANM', ANIMATION: 'ANM',
    MOD: 'MOD', MODEL: 'MOD', MODELING: 'MOD',
    RIG: 'RIG', RIGGING: 'RIG',
    TEX: 'TEX', TEXTURE: 'TEX', TEXTURING: 'TEX',
    LGT: 'LGT', LIGHT: 'LGT', LIGHTING: 'LGT',
    FX: 'FX',
    CMP: 'CMP', COMP: 'CMP', COMPOSITING: 'CMP',
    CON: 'CON', CONCEPT: 'CON',
    STB: 'STB', STORYBOARD: 'STB', ANIMATIC: 'STB',
    EDT: 'EDT', EDIT: 'EDT',
    FNL: 'FNL', FINAL: 'FNL', DELIVERY: 'FNL',
    REND: 'REND', RENDER: 'REND',
    SND: 'SND', SOUND: 'SND', VO: 'SND',
  };
  const FILE_SHOT_RE = /\b(?:sh|shot)[\s_-]?(\d{1,4})\b/i;
  const FILE_VERSION_RE = /\bv(\d{1,4})\b/i;

  const stageToTaskCode = (stage) => {
    const k = String(stage || '').toUpperCase().split(/[\s_-]/)[0];
    return FILE_TASK_CODES[k] || k.slice(0, 3);
  };

  const deriveNomenclatureLabel = (filename, fallbackStage) => {
    const fn = String(filename || '');
    const shot = (fn.match(FILE_SHOT_RE) || [])[1];
    // Pull a task code from any 3-letter uppercase token in the filename.
    let task = '';
    const upTokens = fn.toUpperCase().split(/[^A-Z]/).filter(Boolean);
    for (const t of upTokens) {
      if (FILE_TASK_CODES[t]) { task = FILE_TASK_CODES[t]; break; }
    }
    if (!task) task = stageToTaskCode(fallbackStage);
    const ver = (fn.match(FILE_VERSION_RE) || [])[1] || '001';
    const shotLabel = shot ? `SH_${String(shot).padStart(3, '0')}` : '';
    return {
      shot_label: shotLabel,
      task_code: task,
      version: String(ver).padStart(3, '0'),
      title: [shotLabel, task, `v${String(ver).padStart(3, '0')}`].filter(Boolean).join(' · '),
      shot_num: shot ? parseInt(shot, 10) : 9999,
    };
  };

  const spreadClubbedSubmissions = (subs) => {
    const out = [];
    subs.forEach(s => {
      const media = Array.isArray(s.media) ? s.media : [];
      // Keep single-media submissions as-is (nothing to spread).
      if (media.length <= 1) {
        // Even single-file uploads benefit from the canonical title hint
        // when the artist used a non-standard name. But we DON'T mutate
        // the original — just enrich a display copy.
        if (media.length === 1) {
          const nom = deriveNomenclatureLabel(media[0].filename, s.stage);
          if (nom.shot_label) {
            out.push({ ...s, _display_title: nom.title, _shot_num: nom.shot_num });
            return;
          }
        }
        out.push(s);
        return;
      }
      // Multi-file: each file → its own virtual card.
      media.forEach((m, i) => {
        const nom = deriveNomenclatureLabel(m.filename, s.stage);
        out.push({
          ...s,
          // Make the virtual card uniquely identifiable but tie back to
          // the real submission for approval / comments actions.
          id: `${s.id}__file_${i}`,
          _virtual_id: `${s.id}__file_${i}`,
          _clubbed_parent_id: s.id,
          _clubbed_parent_title: s.title,
          _clubbed_total_files: media.length,
          _clubbed_file_index: i + 1,
          media: [m],
          _display_title: nom.title || (m.filename || `${s.title} · part ${i + 1}`),
          parent_code: nom.shot_label || s.parent_code,
          _shot_num: nom.shot_num,
        });
      });
    });
    return out;
  };

  // For the right pane: organise the visible submissions into
  // Episode → Kind (Characters | Environments | Props | Shots | Other).
  // Episodes are sorted by EP number; kinds in fixed order (chars first).
  const KIND_ORDER = [
    { key: 'character',  label: 'Characters',   color: '#f472b6' },
    { key: 'background', label: 'Environments', color: '#22d3ee' },
    { key: 'prop',       label: 'Props',        color: '#f59e0b' },
    { key: 'shot',       label: 'Shots',        color: '#a78bfa' },
    { key: 'project',    label: 'Project-level', color: '#10b981' },
    { key: 'unsorted',   label: 'Other',        color: '#71717a' },
  ];

  const groupByEpisodeAndKind = (subs) => {
    // Map: episodeName -> { project_id, episode_number, kinds: { kindKey: [subs] } }
    const eps = new Map();
    subs.forEach(s => {
      const epLabel = s.project_name || 'Unknown project';
      const epNum = s.episode_number ?? 9999;
      const epKey = s.project_id || 'unknown';
      if (!eps.has(epKey)) {
        eps.set(epKey, {
          id: epKey,
          name: epLabel,
          episode_number: epNum,
          parent_show_name: s.parent_show_name,
          kinds: {},
        });
      }
      const ep = eps.get(epKey);
      const kind = s.parent_kind || 'unsorted';
      const bucket = KIND_ORDER.find(k => k.key === kind) ? kind : 'unsorted';
      if (!ep.kinds[bucket]) ep.kinds[bucket] = [];
      ep.kinds[bucket].push(s);
    });
    // Sort episodes by EP number, name as tiebreaker
    return Array.from(eps.values()).sort((a, b) => {
      if (a.episode_number !== b.episode_number) return a.episode_number - b.episode_number;
      return (a.name || '').localeCompare(b.name || '');
    });
  };

  // Build the project picker tree once: {shows: [...], episodes_by_show: Map, standalone: [...]}
  const projectPickerTree = useMemo(() => {
    const shows = projectList.filter(p => p.is_show);
    const byParent = new Map();
    for (const ep of projectList.filter(p => p.parent_project_id)) {
      const arr = byParent.get(ep.parent_project_id) || [];
      arr.push(ep);
      byParent.set(ep.parent_project_id, arr);
    }
    for (const arr of byParent.values()) {
      arr.sort((a, b) => (a.episode_number ?? 9999) - (b.episode_number ?? 9999));
    }
    const standalone = projectList.filter(p => !p.is_show && !p.parent_project_id);
    return { shows, byParent, standalone };
  }, [projectList]);

  // --- Actions ------------------------------------------------------------
  const addAnnotation = async (payload) => {
    const { data } = await api.post(`/reviews/${selectedId}/annotations`, { media_index: mediaIdx, ...payload });
    setDetail(d => ({ ...d, annotations: [...(d?.annotations || []), data] }));
  };
  const resolveAnnotation = async (id) => {
    await api.post(`/reviews/annotations/${id}/resolve`);
    setDetail(d => ({ ...d, annotations: d.annotations.map(a => a.id === id ? { ...a, resolved: true } : a) }));
  };
  const deleteAnnotation = async (id) => {
    await api.delete(`/reviews/annotations/${id}`);
    setDetail(d => ({ ...d, annotations: d.annotations.filter(a => a.id !== id) }));
  };

  // Compare View needs a decide-by-id wrapper since it operates on
  // submissions other than the one currently open in the right-pane
  // detail view. Reuses the same `/internal-decision|pm-decision|
  // client-decision` paths so the producer / PM / client gates all
  // continue to function identically.
  const decideForCompare = async (subId, decision) => {
    const path = mode === 'supervisor' ? 'internal-decision'
               : mode === 'pm'         ? 'pm-decision'
               :                         'client-decision';
    await api.post(`/reviews/${subId}/${path}`, { decision, note: '' });
    await loadInbox();
  };

  const decide = async (decision) => {
    setBusy(true); setMsg('');
    try {
      const path = mode === 'supervisor' ? 'internal-decision'
                 : mode === 'pm'         ? 'pm-decision'
                 :                         'client-decision';
      await api.post(`/reviews/${selectedId}/${path}`, { decision, note: decisionNote });
      setMsg(`Marked as ${decision}`);
      setDecisionNote('');
      backToInbox();
      loadInbox();
    } catch (e) {
      setMsg(e.response?.data?.detail || 'Failed');
    }
    setBusy(false);
  };

  // 🛡️ EXPRESS OVERRIDE — master_admin only. Bypasses Supervisor + PM gates
  // when both reviewers are unavailable. Hard-required reason.
  const masterOverride = async (reason) => {
    setBusy(true); setMsg('');
    try {
      await api.post(`/reviews/${selectedId}/master-admin-override`, { reason });
      setMsg('Override applied — submission is now with the client.');
      setDecisionNote('');
      backToInbox();
      loadInbox();
    } catch (e) {
      setMsg(e.response?.data?.detail || 'Override failed');
    }
    setBusy(false);
  };

  // ─── UI ─────────────────────────────────────────────────────────────────
  // Compute visibleSubs + episodeGroups BEFORE any conditional early return
  // (React hooks must always be called in the same order).
  const visibleSubs = useMemo(() => {
    let base = selectedDept
      ? (groupedByDept[selectedDept] || [])
      : filteredInbox;
    // Apply artist filter when active (only within the matching dept).
    if (selectedArtist && selectedArtist.dept_key === selectedDept) {
      base = base.filter(s =>
        (s.submitter_id || s.created_by || 'unknown') === selectedArtist.submitter_id
      );
      // Spread clubbed uploads (multi-file submissions) into per-shot
      // virtual cards with nomenclature-derived titles. This is the
      // founder's "spread individually + rename" rule for artist views.
      base = spreadClubbedSubmissions(base);
      // Sort by extracted shot number so the founder sees SH_006,
      // SH_007, SH_008 in proper sequence rather than recency order.
      base = [...base].sort((a, b) =>
        ((a._shot_num ?? shotSeqOf(a)) - (b._shot_num ?? shotSeqOf(b)))
      );
    }
    return base;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDept, selectedArtist, groupedByDept, filteredInbox]);

  const episodeGroups = useMemo(() => groupByEpisodeAndKind(visibleSubs), [visibleSubs]);

  if (selectedId && detail) {
    return renderDetail();
  }

  return (
    <div className="h-[calc(100vh-64px)] flex flex-col" data-testid="review-inbox">
      {/* Top bar */}
      <div className="flex items-center justify-between border-b border-zinc-800 px-5 py-3">
        <div>
          <h1 className="text-xl font-bold text-white tracking-tight flex items-center gap-2">
            <Inbox className="h-5 w-5 text-amber-400"/> Approvals Inbox
          </h1>
          <p className="text-[11px] text-zinc-500">
            {visibleSubs.length} submission{visibleSubs.length !== 1 ? 's' : ''}
            {mode === 'feedback'
              ? ' needing your response (client requested changes)'
              : ' awaiting your review'}
            {selectedDept && <> · filtered by <span className="text-amber-300">{DEPTS.find(d=>d.key===selectedDept)?.label}</span></>}
            {projectFilter !== 'all' && projectList.find(p => p.id === projectFilter) && (
              <> · in <span className="text-cyan-300">{projectList.find(p => p.id === projectFilter)?.name}</span></>
            )}
          </p>
          {(isPM || isSupervisor || user?.role === 'master_admin') && (
            <button
              onClick={async () => {
                // Find the project_id from any visible submission so the cleanup is scoped.
                const pid = (visibleSubs[0] || inbox[0])?.project_id;
                if (!pid) { alert('No submissions in view to clean up.'); return; }
                if (!window.confirm('Scan this project for duplicate submissions and delete the older copies?\n\nThis is safe — only the most recent of each duplicate set is kept.')) return;
                try {
                  const r = await api.post('/reviews/cleanup-duplicates', { project_id: pid });
                  alert(`Cleanup done.\n• Duplicate groups found: ${r.data.groups_with_duplicates}\n• Older copies removed: ${r.data.total_duplicates_removed}`);
                  loadInbox();
                } catch (e) { alert(e.response?.data?.detail || 'Cleanup failed'); }
              }}
              data-testid="approvals-cleanup-dupes"
              className="mt-1 text-[10px] text-rose-300 hover:text-rose-200 underline decoration-dotted underline-offset-2"
              title="Bulk-deletes duplicate submissions (keeps the latest of each group)"
            >
              Clean up duplicate submissions in this project
            </button>
          )}
        </div>
        <div className="flex items-center gap-3">
          {/* Project picker — always rendered (don't hide if list briefly empty during load).
              Show wraps episodes as <optgroup> so clients can filter to a specific show or episode. */}
          <div className="flex flex-col items-end gap-0.5" data-testid="review-project-picker-wrap">
            <label className="text-[9px] uppercase tracking-widest text-zinc-500">Project</label>
            <select
              value={projectFilter}
              onChange={e => setProjectFilter(e.target.value)}
              data-testid="review-project-picker"
              disabled={projectList.length === 0}
              className="h-8 bg-zinc-900 border border-zinc-800 rounded-md text-xs text-zinc-200 px-3 focus:outline-none focus:border-cyan-500/50 min-w-[15rem] disabled:opacity-50">
              <option value="all">All projects ({inbox.length})</option>
              {projectList.length === 0 && (
                <option value="loading" disabled>Loading projects…</option>
              )}
              {projectPickerTree.standalone.length > 0 && (
                <optgroup label="── Standalone Projects ──">
                  {projectPickerTree.standalone.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </optgroup>
              )}
              {projectPickerTree.shows.map(show => {
                const eps = projectPickerTree.byParent.get(show.id) || [];
                return (
                  <optgroup key={show.id} label={`📺 ${show.name}`}>
                    <option value={show.id}>↳ all episodes ({eps.length})</option>
                    {eps.map((ep, i) => (
                      <option key={ep.id} value={ep.id}>
                        &nbsp;&nbsp;&nbsp;EP{String(ep.episode_number ?? (i + 1)).padStart(2, '0')} · {ep.name}
                      </option>
                    ))}
                  </optgroup>
                );
              })}
            </select>
          </div>
        {((isSupervisor ? 1 : 0) + (isPM ? 1 : 0) + (isClient ? 1 : 0)) > 1 && (
          <div className="flex rounded-lg border border-zinc-800 bg-zinc-900 p-0.5" data-testid="mode-toggle">
            {isSupervisor && (
              <button onClick={()=>setMode('supervisor')}
                className={`px-4 py-1.5 text-xs rounded flex items-center gap-1.5 ${mode==='supervisor'?'bg-amber-400 text-zinc-900 font-semibold':'text-zinc-400 hover:text-white'}`}
                data-testid="mode-supervisor"
              >
                Supervisor Gate
                {gateCounts.supervisor > 0 && (
                  <span data-testid="mode-supervisor-count" className={`min-w-[18px] px-1.5 py-0.5 rounded-full text-[10px] font-bold leading-none ${mode==='supervisor' ? 'bg-zinc-900 text-amber-300' : 'bg-amber-500/20 text-amber-300 border border-amber-500/40'}`}>
                    {gateCounts.supervisor}
                  </span>
                )}
              </button>
            )}
            {isPM && (
              <button onClick={()=>setMode('pm')}
                className={`px-4 py-1.5 text-xs rounded flex items-center gap-1.5 ${mode==='pm'?'bg-amber-400 text-zinc-900 font-semibold':'text-zinc-400 hover:text-white'}`}
                data-testid="mode-pm"
              >
                Producer Gate
                {gateCounts.pm > 0 && (
                  <span data-testid="mode-pm-count" className={`min-w-[18px] px-1.5 py-0.5 rounded-full text-[10px] font-bold leading-none ${mode==='pm' ? 'bg-zinc-900 text-amber-300' : 'bg-amber-500/20 text-amber-300 border border-amber-500/40'}`}>
                    {gateCounts.pm}
                  </span>
                )}
              </button>
            )}
            {(isClient || isSupervisor || isPM) && (
              <button onClick={()=>setMode('client')}
                className={`px-4 py-1.5 text-xs rounded flex items-center gap-1.5 ${mode==='client'?'bg-amber-400 text-zinc-900 font-semibold':'text-zinc-400 hover:text-white'}`}
                data-testid="mode-client"
              >
                Client Gate
                {gateCounts.client > 0 && (
                  <span data-testid="mode-client-count" className={`min-w-[18px] px-1.5 py-0.5 rounded-full text-[10px] font-bold leading-none ${mode==='client' ? 'bg-zinc-900 text-amber-300' : 'bg-amber-500/20 text-amber-300 border border-amber-500/40'}`}>
                    {gateCounts.client}
                  </span>
                )}
              </button>
            )}
            {(isSupervisor || isPM) && (
              <button onClick={()=>setMode('feedback')}
                className={`px-4 py-1.5 text-xs rounded flex items-center gap-1.5 ${mode==='feedback'?'bg-rose-500 text-white font-semibold':'text-rose-300 hover:text-white hover:bg-rose-500/15'}`}
                data-testid="mode-feedback"
                title="Client decisions (revise / rejected) that need your response"
              >
                <MessageSquare className="w-3 h-3" />
                Client Feedback
                {gateCounts.feedback > 0 && (
                  <span data-testid="mode-feedback-count" className={`min-w-[18px] px-1.5 py-0.5 rounded-full text-[10px] font-bold leading-none ${mode==='feedback' ? 'bg-white/80 text-rose-700' : 'bg-rose-500/20 text-rose-300 border border-rose-500/40'}`}>
                    {gateCounts.feedback}
                  </span>
                )}
              </button>
            )}
          </div>
        )}
        </div>
      </div>

      {/* Status filter pills — show in EVERY gate (Supervisor / Producer /
          Client / Feedback) so admins, PMs, and clients can all pivot
          between Pending / Approved / Rejected / All History instead of
          only seeing pending. */}
      <div className="px-5 py-2 border-b border-zinc-800 bg-zinc-950/40 flex items-center gap-1.5 flex-wrap" data-testid="client-history-filter">
        <span className="text-[9px] uppercase tracking-widest text-zinc-500 mr-2">View:</span>
        {[
          { k: 'pending',  l: 'Pending Review',    icon: '⏳', activeCls: 'bg-amber-500/20 border-amber-500/50 text-amber-200' },
          { k: 'approved', l: 'Approved',          icon: '✓',  activeCls: 'bg-emerald-500/20 border-emerald-500/50 text-emerald-200' },
          { k: 'revise',   l: 'Changes Requested', icon: '↺',  activeCls: 'bg-rose-500/20 border-rose-500/50 text-rose-200' },
          { k: 'rejected', l: 'Rejected',          icon: '✕',  activeCls: 'bg-red-500/20 border-red-500/50 text-red-200' },
          { k: 'all',      l: 'All History',       icon: '⌘',  activeCls: 'bg-zinc-700 border-zinc-600 text-white' },
        ].map(p => {
          const active = historyView === p.k;
          return (
            <button key={p.k} onClick={() => setHistoryView(p.k)}
              data-testid={`history-pill-${p.k}`}
              className={`text-[11px] px-2.5 py-1 rounded-full border transition flex items-center gap-1.5 ${
                active ? p.activeCls : 'bg-zinc-900 border-zinc-800 text-zinc-400 hover:text-white hover:border-zinc-700'
              }`}>
              <span>{p.icon}</span>{p.l}
            </button>
          );
        })}
        <span className="ml-auto text-[10px] text-zinc-500">
          History filter applies to <span className="text-amber-300">Client Gate</span> view only — switches the inbox between pending/decided submissions.
        </span>
      </div>

      {/* Split panes */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left — 11-department tree */}
        <aside className="w-64 shrink-0 border-r border-zinc-800 bg-zinc-950/50 overflow-y-auto" data-testid="dept-tree">
          <div className="p-3">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 px-2 mb-2">Departments</div>
            <button
              onClick={()=>setSelectedDept(null)}
              data-testid="dept-all"
              className={`w-full flex items-center justify-between text-left px-2.5 py-2 rounded-md text-xs mb-1 transition-colors ${
                !selectedDept ? 'bg-amber-400/10 text-amber-300 border border-amber-400/30' : 'hover:bg-zinc-900 text-zinc-300 border border-transparent'
              }`}>
              <span className="flex items-center gap-2"><Sparkles className="h-3 w-3"/> All departments</span>
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${filteredInbox.length > 0 ? 'bg-amber-500 text-zinc-900 font-semibold' : 'bg-zinc-800 text-zinc-500'}`}>{filteredInbox.length}</span>
            </button>
            {DEPTS.map(d => {
              const count = groupedByDept[d.key]?.length || 0;
              const active = selectedDept === d.key;
              const isExpanded = expandedDept === d.key && count > 0;
              const artists = artistsByDept[d.key] || [];
              return (
                <div key={d.key}>
                  <button
                    onClick={() => {
                      // Clicking the dept row both selects the dept (filters
                      // the grid to its submissions) AND toggles the expanded
                      // artist list. This matches the founder's mental model:
                      // one click = "show me this dept's stuff".
                      const willActivate = !active;
                      setSelectedDept(willActivate ? d.key : null);
                      setExpandedDept(willActivate ? d.key : null);
                      setSelectedArtist(null);
                      setLineupDept(null);
                    }}
                    data-testid={`dept-${d.key}`}
                    className={`w-full flex items-center justify-between text-left px-2.5 py-2 rounded-md text-xs mb-0.5 transition-colors ${
                      active ? 'bg-zinc-800/80 text-white' : count > 0 ? 'hover:bg-zinc-900 text-zinc-200' : 'hover:bg-zinc-900/50 text-zinc-600'
                    }`}>
                    <span className="flex items-center gap-2 min-w-0">
                      <span className="h-2 w-2 rounded-sm shrink-0" style={{ background: d.color }}/>
                      <span className="truncate">{d.label}</span>
                      {count > 0 && (
                        <span className={`shrink-0 transition-transform ${isExpanded ? 'rotate-90' : ''}`} aria-hidden>›</span>
                      )}
                    </span>
                    {count > 0 ? (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-rose-500 text-white font-bold shrink-0 ml-2">{count}</span>
                    ) : (
                      <span className="text-[10px] text-zinc-700 shrink-0 ml-2">0</span>
                    )}
                  </button>

                  {/* Expanded children: Full Lineup + Artists */}
                  {isExpanded && (
                    <div className="pl-4 pb-1 space-y-0.5" data-testid={`dept-${d.key}-children`}>
                      {/* Full Lineup — internal-only, hidden from client role */}
                      {user?.role !== 'client' && (
                        <button
                          onClick={() => {
                            setLineupDept(d.key);
                            setSelectedArtist(null);
                          }}
                          data-testid={`dept-${d.key}-lineup`}
                          className={`w-full flex items-center justify-between text-left px-2.5 py-1.5 rounded-md text-[11px] transition-colors ${
                            lineupDept === d.key
                              ? 'bg-violet-500/20 text-violet-200 border border-violet-500/40'
                              : 'hover:bg-violet-500/10 text-violet-300 border border-transparent'
                          }`}>
                          <span className="flex items-center gap-1.5">
                            <Sparkles className="h-3 w-3 shrink-0"/>
                            <span className="font-medium uppercase tracking-wider text-[10px]">Full Lineup</span>
                          </span>
                          <span className="text-[9px] text-violet-300/70 uppercase tracking-wider">internal</span>
                        </button>
                      )}
                      {/* Artist rows */}
                      {artists.map((a, idx) => {
                        const vendor = isVendorRole(a.role);
                        const sel = selectedArtist?.submitter_id === a.submitter_id
                          && selectedArtist?.dept_key === d.key;
                        // Visual divider between internal artists and vendors
                        const prev = idx > 0 ? artists[idx - 1] : null;
                        const showDivider = prev && !isVendorRole(prev.role) && vendor;
                        return (
                          <React.Fragment key={a.submitter_id}>
                            {showDivider && (
                              <div className="px-2.5 py-1 text-[9px] uppercase tracking-widest text-zinc-600 border-t border-zinc-800/50 mt-1">Vendors</div>
                            )}
                            <button
                              onClick={() => {
                                setSelectedArtist(sel ? null : { dept_key: d.key, ...a });
                                setLineupDept(null);
                              }}
                              data-testid={`dept-${d.key}-artist-${a.submitter_id}`}
                              className={`w-full flex items-center justify-between text-left px-2.5 py-1.5 rounded-md text-[11px] transition-colors ${
                                sel
                                  ? 'bg-amber-500/20 text-amber-200 border border-amber-500/40'
                                  : 'hover:bg-zinc-900 text-zinc-300 border border-transparent'
                              }`}>
                              <span className="flex items-center gap-1.5 min-w-0">
                                <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${vendor ? 'bg-cyan-400' : 'bg-emerald-400'}`}/>
                                <span className="truncate">{a.name}</span>
                              </span>
                              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-zinc-800 text-zinc-300 font-mono ml-2 shrink-0">{a.count}</span>
                            </button>
                          </React.Fragment>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
            {groupedByDept.__unmatched__.length > 0 && (
              <button
                onClick={() => setSelectedDept(selectedDept === '__unmatched__' ? null : '__unmatched__')}
                className={`w-full flex items-center justify-between text-left px-2.5 py-2 rounded-md text-xs mt-2 border-t border-zinc-800 pt-3 ${
                  selectedDept === '__unmatched__' ? 'bg-zinc-800/80 text-white' : 'hover:bg-zinc-900 text-zinc-400'
                }`}>
                <span>Other / Uncategorized</span>
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-zinc-700 text-white">{groupedByDept.__unmatched__.length}</span>
              </button>
            )}
          </div>
        </aside>

        {/* Right — submissions for selected dept */}
        <main className="flex-1 overflow-y-auto p-5">
          {lineupDept ? (
            <LineupPlayer
              submissions={groupedByDept[lineupDept] || []}
              departmentLabel={DEPTS.find(d => d.key === lineupDept)?.label || ''}
              api={api}
              mediaUrlFor={resolveMediaUrl}
            />
          ) : selectedDept === 'animation' ? (
            /* Founder spec May 23 2026 + Jun 24 2026: Layout & Animation
             * tab supports TWO review modes —
             *   • 'lineup'  — single-window full episode player with
             *     annotations + frame-by-frame (default for clients,
             *     replaces the legacy Animatics review flow).
             *   • 'compare' — SyncSketch-style two-column shot-by-shot
             *     review (default for internal QA roles).
             * The toggle below lets internal roles flip between them
             * and includes a "View as client" preview button. */
            <div className="flex flex-col h-full" data-testid="anim-panel">
              {/* Toggle bar */}
              <div className="border-b border-zinc-800 bg-zinc-950/70 px-5 py-2.5 flex items-center gap-3"
                    data-testid="anim-view-toggle">
                <div className="inline-flex bg-zinc-900 border border-zinc-800 rounded overflow-hidden">
                  <button
                    onClick={() => setAnimationView('lineup')}
                    className={`px-3 py-1.5 text-xs font-semibold ${
                      animationView === 'lineup'
                        ? 'bg-amber-500/15 text-amber-400'
                        : 'text-zinc-400 hover:text-zinc-200'
                    }`}
                    data-testid="anim-view-lineup-btn"
                  >
                    Lineup (full episode)
                  </button>
                  <button
                    onClick={() => setAnimationView('compare')}
                    className={`px-3 py-1.5 text-xs font-semibold border-l border-zinc-800 ${
                      animationView === 'compare'
                        ? 'bg-amber-500/15 text-amber-400'
                        : 'text-zinc-400 hover:text-zinc-200'
                    }`}
                    data-testid="anim-view-compare-btn"
                  >
                    Shot-by-shot (compare)
                  </button>
                </div>
                {!isClientRole && (
                  <label className="flex items-center gap-1.5 text-[11px] text-zinc-400 cursor-pointer ml-auto"
                          data-testid="view-as-client-toggle">
                    <input
                      type="checkbox"
                      checked={viewAsClient}
                      onChange={(e) => {
                        setViewAsClient(e.target.checked);
                        if (e.target.checked) setAnimationView('lineup');
                      }}
                      className="accent-amber-500"
                    />
                    <span>View as client</span>
                  </label>
                )}
              </div>
              {/* Body */}
              <div className="flex-1 overflow-hidden">
                {animationView === 'lineup' ? (
                  <LineupReviewPanel
                    submissions={groupedByDept.animation || []}
                    api={api}
                    mediaUrlFor={resolveMediaUrl}
                    onDecide={async (submissionId, decision, note) => {
                      const path = decision === 'approved'
                        ? 'approve'
                        : (decision === 'changes_requested' ? 'request-changes' : 'reject');
                      await api.post(`/reviews/${submissionId}/${path}`,
                        { decision, note });
                      // Refresh inbox so the lineup card moves from
                      // pending → approved/changes immediately.
                      try { await loadInbox(); } catch (_e) { /* noop */ }
                    }}
                    user={user}
                  />
                ) : (
                  <CompareView
                    submissions={compareSubmissionsForClientGate}
                    artistsByDept={artistsByDeptForCompare}
                    projectId={(() => {
                      if (projectFilter && projectFilter !== 'all') return projectFilter;
                      return (compareSubmissionsForClientGate || [])[0]?.project_id
                          || (groupedByDept.animation || [])[0]?.project_id
                          || '';
                    })()}
                    dept="animation"
                    onDecide={decideForCompare}
                    mediaUrlFor={resolveMediaUrl}
                  />
                )}
              </div>
            </div>
          ) : visibleSubs.length === 0 ? (
            <EmptyState mode={mode} />
          ) : (
            <div className="space-y-6" data-testid="review-episode-groups">
              {episodeGroups.map(ep => {
                const totalInEp = Object.values(ep.kinds).reduce((s, arr) => s + arr.length, 0);
                return (
                  <div key={ep.id} className="border border-zinc-800 rounded-lg overflow-hidden bg-zinc-950/40" data-testid={`episode-group-${ep.id}`}>
                    {/* Episode header */}
                    <div className="px-4 py-2.5 bg-gradient-to-r from-violet-500/10 via-zinc-900 to-zinc-900 border-b border-zinc-800 flex items-baseline gap-2">
                      <span className="text-[10px] uppercase tracking-widest text-violet-300 font-semibold">Episode</span>
                      <h3 className="text-sm font-bold text-white">{ep.name}</h3>
                      {ep.parent_show_name && (
                        <span className="text-[10px] text-zinc-500">↳ from {ep.parent_show_name}</span>
                      )}
                      <span className="ml-auto text-[10px] text-zinc-400 font-mono">{totalInEp} item{totalInEp !== 1 ? 's' : ''}</span>
                    </div>
                    {/* Kind sub-sections */}
                    <div className="divide-y divide-zinc-800/40">
                      {KIND_ORDER.filter(k => (ep.kinds[k.key] || []).length > 0).map(kind => (
                        <div key={kind.key} className="p-3" data-testid={`kind-section-${ep.id}-${kind.key}`}>
                          <div className="flex items-center gap-2 mb-2.5 px-1">
                            <span className="h-2 w-2 rounded-sm" style={{ background: kind.color }} />
                            <h4 className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: kind.color }}>
                              {kind.label}
                            </h4>
                            <span className="text-[10px] font-mono text-zinc-500">({(ep.kinds[kind.key] || []).length})</span>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                            {(ep.kinds[kind.key] || []).map(sub => (
                              <InboxCard
                                key={sub.id}
                                sub={sub}
                                onOpen={() => openSubmission(sub.id)}
                                onAuditTrail={sub.parent_id ? () => setAuditTrailAssetId(sub.parent_id) : null}
                                canDelete={isPM || isSupervisor || user?.role === 'master_admin'}
                                historyView={historyView}
                                onMoveToPending={moveToPending}
                                onDelete={async () => {
                                  if (!window.confirm(`Delete submission "${sub.title}"?\n\nThis is permanent. Use this to clean up duplicate uploads.`)) return;
                                  try {
                                    await api.delete(`/reviews/submissions/${sub.id}`);
                                    await loadInbox();
                                  } catch (e) { alert(e.response?.data?.detail || 'Delete failed'); }
                                }}
                              />
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </main>
      </div>
      {auditTrailAssetId && (
        <AssetAuditTrailModal
          assetId={auditTrailAssetId}
          api={api}
          onClose={() => setAuditTrailAssetId(null)}
        />
      )}
    </div>
  );

  function renderDetail() {
    const sub = detail.submission;
    const m = sub.media?.[mediaIdx];
    const url = mediaUrls[mediaIdx];

    return (
      <div className="flex flex-col h-[calc(100vh-64px)]" data-testid="review-detail">
        {/* Top bar */}
        <div className="flex items-center gap-3 border-b border-zinc-800 bg-zinc-950 px-5 py-3">
          <button onClick={backToInbox} className="text-zinc-400 hover:text-white flex items-center gap-1 text-sm" data-testid="back-btn">
            <ArrowLeft className="h-4 w-4"/> Back
          </button>
          <div className="ml-3">
            <h2 className="text-sm font-semibold text-white">{sub.title}</h2>
            <div className="flex items-center gap-2 text-[11px] text-zinc-500">
              <StagePill stage={sub.stage} />
              <span>·</span>
              <span>by <span className="text-zinc-200 font-medium">{displayNameFor(sub, user?.role)}</span>{shouldRenderHandlePill(sub, user?.role) ? <span className="ml-1 text-cyan-400 font-mono">@{handleFor(sub)}</span> : null}</span>
              <span>·</span>
              <span>Round {sub.round}</span>
              {sub.requires_internal_review && <><span>·</span><span className="text-violet-300">two-stage</span></>}
            </div>
          </div>
          <div className="flex-1" />
          <StatusBadge status={sub.status} />
        </div>

        {/* Main split */}
        <div className="flex flex-1 overflow-hidden">
          {/* Left — media viewer */}
          <div className="flex-1 overflow-y-auto p-5 space-y-4 bg-gradient-to-b from-zinc-950 to-black">
            {/* Media tabs */}
            {sub.media?.length > 1 && (
              <div className="flex gap-1 overflow-x-auto pb-1">
                {sub.media.map((mm, i) => (
                  <button key={i} onClick={()=>setMediaIdx(i)}
                    className={`shrink-0 flex items-center gap-2 px-3 py-1.5 rounded-md text-xs transition-colors ${mediaIdx===i ? 'bg-amber-400 text-zinc-900' : 'bg-zinc-900 text-zinc-400 hover:text-white'}`}
                    data-testid={`media-tab-${i}`}
                    title={isSourceFile(mm) ? 'Source file — not previewable in browser (download to open in Blender / Maya / etc.)' : ''}
                  >
                    {is3DModel(mm) ? <Box className="h-3 w-3"/> : mm.type === 'video' ? <Film className="h-3 w-3"/> : mm.type === 'audio' ? <Music className="h-3 w-3"/> : <ImageIcon className="h-3 w-3"/>}
                    {mm.filename?.slice(0, 18)}
                    {isSourceFile(mm) && (
                      <span className={`ml-1 px-1 py-0.5 rounded text-[8px] font-bold uppercase tracking-wider ${mediaIdx===i ? 'bg-zinc-900/30 text-zinc-900' : 'bg-zinc-700 text-zinc-300'}`}>
                        Source
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}

            {url ? (
              <>
                {m?.type === 'video' && m?.transcode?.applied && (
                  <div className="flex items-center gap-2 text-[10px] text-emerald-300 bg-emerald-500/5 border border-emerald-500/20 rounded-md px-3 py-1.5" data-testid="transcode-badge">
                    <Sparkles className="h-3 w-3"/>
                    <span>Web proxy: <strong>{m.transcode.output?.width || '—'}×{m.transcode.output?.height || '—'}</strong></span>
                    <span className="text-zinc-500">·</span>
                    <span>preset <strong>{m.transcode.preset}</strong></span>
                    <span className="text-zinc-500">·</span>
                    <span>size −<strong>{m.transcode.reduction_pct}%</strong></span>
                    <span className="text-zinc-600 ml-auto">original preserved on NAS</span>
                  </div>
                )}
                {isSourceFile(m) ? (
                  <div className="h-[460px] rounded-xl border border-dashed border-amber-500/40 bg-amber-500/5 flex flex-col items-center justify-center gap-3 px-6 text-center" data-testid="source-file-notice">
                    <Box className="w-10 h-10 text-amber-400" />
                    <div>
                      <h3 className="text-sm font-semibold text-amber-300">
                        Source file — not previewable in browser
                      </h3>
                      <p className="text-xs text-zinc-400 mt-1 max-w-md">
                        <span className="font-mono text-amber-200">{m.filename}</span> is a working file from{' '}
                        {/\.blend$/i.test(m.filename || '') ? 'Blender'
                          : /\.(ma|mb)$/i.test(m.filename || '') ? 'Maya'
                          : /\.psd$/i.test(m.filename || '') ? 'Photoshop'
                          : /\.max$/i.test(m.filename || '') ? '3ds Max'
                          : /\.c4d$/i.test(m.filename || '') ? 'Cinema 4D'
                          : 'a desktop application'}.
                        It can&apos;t be rendered in a web viewer.
                      </p>
                    </div>
                    {(() => {
                      const altIdx = (sub.media || []).findIndex((mm, i) => i !== mediaIdx && isPreviewable(mm));
                      if (altIdx >= 0) {
                        const alt = sub.media[altIdx];
                        return (
                          <button
                            onClick={() => setMediaIdx(altIdx)}
                            data-testid="source-file-jump-preview"
                            className="h-9 px-4 bg-amber-500 hover:bg-amber-400 text-zinc-950 text-xs font-bold rounded-md transition-colors"
                          >
                            View the preview file → {alt.filename}
                          </button>
                        );
                      }
                      return (
                        <p className="text-[10px] text-zinc-500 italic">
                          No preview file attached. Ask the artist to also upload a .glb / image / mp4 for review.
                        </p>
                      );
                    })()}
                    {(m.nas || {}).path && (
                      <p className="text-[10px] text-zinc-600 font-mono mt-2">
                        NAS: {m.nas.path}
                      </p>
                    )}
                  </div>
                ) : is3DModel(m) ? (
                  <div className="h-[640px] rounded-xl overflow-hidden border border-zinc-800" data-testid="3d-viewer-container">
                    <Asset3DReviewSplit reference={reference} onReload={reloadReference}>
                      <ModelViewer3D
                        src={url}
                        format={get3DFormat(m)}
                        pins={pmOnly ? [] : (detail.annotations || [])
                          .filter(a => a.media_index === mediaIdx && a.point3d)
                          .map(a => ({
                            id: a.id,
                            x: a.point3d.x, y: a.point3d.y, z: a.point3d.z,
                            label: a.text || '',
                          }))}
                        onPinsChange={pmOnly ? null : async (nextPins) => {
                          // ModelViewer3D may give us either a freshly-placed
                          // pin (without server id) or a delete (id missing
                          // from the next list). Diff against current state.
                          const existing = (detail.annotations || [])
                            .filter(a => a.media_index === mediaIdx && a.point3d);
                          const existingById = new Map(existing.map(a => [a.id, a]));
                          const nextById = new Map(nextPins.map(p => [p.id, p]));
                          // Adds — pins in next without a matching existing annotation
                          for (const p of nextPins) {
                            if (!existingById.has(p.id) && !p.id?.startsWith('ann_')) {
                              await addAnnotation({
                                text: p.label || '3D pin',
                                point3d: { x: p.x, y: p.y, z: p.z },
                              });
                            }
                          }
                          // Removals — existing ids not in next
                          for (const a of existing) {
                            if (!nextById.has(a.id)) {
                              await deleteAnnotation(a.id);
                            }
                          }
                        }}
                      />
                    </Asset3DReviewSplit>
                  </div>
                ) : (
                  <MediaAnnotator
                    src={url}
                    type={m.type}
                    annotations={(detail.annotations || []).filter(a => a.media_index === mediaIdx)}
                    onAdd={pmOnly ? null : addAnnotation}
                    onResolve={pmOnly ? null : resolveAnnotation}
                    onDelete={pmOnly ? null : deleteAnnotation}
                    readOnly={pmOnly}
                    viewerRole={user?.role}
                    activeAnnotationId={activeAnnId}
                    seekTick={seekTick}
                    onActiveAnnotationChange={(id) => {
                      setActiveAnnId(id);
                      if (id) setSeekTick(t => t + 1);
                    }}
                  />
                )}
              </>
            ) : (
              <div className="h-96 flex items-center justify-center text-zinc-500 text-sm">Loading media…</div>
            )}

            {/* Artist brief */}
            {sub.description && (
              <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-3">
                <div className="text-[11px] text-zinc-500 mb-1 flex items-center gap-1"><Sparkles className="h-3 w-3"/>Artist's note</div>
                <p className="text-sm text-zinc-200 whitespace-pre-wrap">{sub.description}</p>
              </div>
            )}
          </div>

          {/* Right — annotations + decision */}
          <div className="w-96 border-l border-zinc-800 bg-zinc-950 flex flex-col overflow-hidden">
            {/* Annotation filters */}
            <div className="flex items-center gap-2 border-b border-zinc-800 px-4 py-3">
              <MessageSquare className="h-4 w-4 text-amber-400"/>
              <span className="text-sm font-semibold text-white">Notes</span>
              <span className="text-[11px] text-zinc-500">({annotations.length})</span>
              <div className="flex-1" />
              <div className="flex rounded border border-zinc-800 text-[10px]">
                {['all','open','resolved'].map(f => (
                  <button key={f} onClick={()=>setFilter(f)}
                    className={`px-2 py-1 ${filter===f?'bg-amber-400 text-zinc-900':'text-zinc-400 hover:text-white'}`}
                    data-testid={`filter-${f}`}
                  >{f}</button>
                ))}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto">
              {annotations.length === 0 ? (
                <div className="px-6 py-10 text-center">
                  <Filter className="h-6 w-6 text-zinc-700 mx-auto mb-2"/>
                  <p className="text-xs text-zinc-500">No {filter !== 'all' ? filter : ''} notes yet.</p>
                  <p className="text-[10px] text-zinc-600 mt-1">Use tools above the media to draw, pin, or annotate.</p>
                </div>
              ) : (
                <ul className="divide-y divide-zinc-800">
                  {annotations.map((a, idx) => (
                    <AnnotationRow
                      key={a.id}
                      a={a}
                      idx={idx+1}
                      mediaUrl={url}
                      mediaType={m?.type || 'image'}
                      onResolve={resolveAnnotation}
                      onDelete={deleteAnnotation}
                      isActive={activeAnnId === a.id}
                      onActivate={() => activateAnn(a.id)}
                    />
                  ))}
                </ul>
              )}
            </div>

            {/* Gate-mismatch warning — when the user has switched to a mode
                that doesn't match the submission's current status, surface a
                clear hint instead of letting them hit the API and get a
                confusing 400 error. */}
            {detail?.submission?.status === 'supervisor_review' && mode === 'pm' && (
              <div className="mx-4 mt-3 px-3 py-2 bg-amber-500/10 border border-amber-500/40 rounded-sm text-[10px] text-amber-200" data-testid="gate-mismatch-pm-on-supervisor">
                <strong>Step 1 of 3:</strong> This is at <span className="font-mono">supervisor_review</span>. Switch to <span className="font-semibold">Supervisor Gate</span> tab and click <span className="font-semibold">Approve → Producer</span> first. Only then will the Producer gate unlock to send it to the client.
              </div>
            )}
            {detail?.submission?.status === 'pm_review' && mode === 'supervisor' && (
              <div className="mx-4 mt-3 px-3 py-2 bg-violet-500/10 border border-violet-500/40 rounded-sm text-[10px] text-violet-200" data-testid="gate-mismatch-supervisor-on-pm">
                <strong>Step 2 of 3:</strong> Already through Supervisor. Switch to <span className="font-semibold">Producer Gate</span> tab and click <span className="font-semibold">Send for Approval → Client</span> to push to the client.
              </div>
            )}

            {/* Decision bar */}
            <div className="border-t border-zinc-800 bg-zinc-950 p-4 space-y-2">
              <label className="text-[10px] text-zinc-500 uppercase tracking-wider">Decision note (optional)</label>
              <textarea value={decisionNote} onChange={e=>setDecisionNote(e.target.value)} rows={2} className="w-full bg-zinc-900 border border-zinc-800 rounded text-xs text-white p-2" placeholder="Summary for the artist…" data-testid="decision-note"/>
              {mode === 'supervisor' ? (
                <div className="grid grid-cols-2 gap-2">
                  <button onClick={()=>decide('revise')} disabled={busy} className="h-10 rounded bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/40 text-amber-300 text-xs font-semibold flex items-center justify-center gap-1" data-testid="act-revise"><RotateCcw className="h-3.5 w-3.5"/>Revise</button>
                  <button onClick={()=>decide('approved')} disabled={busy} className="h-10 rounded bg-emerald-500 hover:bg-emerald-400 text-zinc-900 text-xs font-bold flex items-center justify-center gap-1" data-testid="act-approve"><ChevronRight className="h-3.5 w-3.5"/>Approve → Producer</button>
                </div>
              ) : mode === 'pm' ? (
                <>
                  {pmOnly && (
                    <div className="rounded bg-sky-500/10 border border-sky-500/30 p-2 text-[10px] text-sky-200 mb-1" data-testid="pm-readonly-notice">
                      <ShieldCheck className="inline h-3 w-3 mr-1"/>Read-only Producer gate — corrections already captured by Supervisor / Creative Director above.
                    </div>
                  )}
                  <div className="grid grid-cols-5 gap-2">
                    <button onClick={()=>decide('revise')} disabled={busy} className="col-span-1 h-12 rounded bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/40 text-amber-300 text-xs font-semibold flex items-center justify-center gap-1" data-testid="act-revise" title="Bounce back to Supervisor + Artist">
                      <RotateCcw className="h-3.5 w-3.5"/>Bounce
                    </button>
                    <button
                      onClick={()=>decide('approved')}
                      disabled={busy}
                      data-testid="act-send-for-approval"
                      className="col-span-4 h-12 rounded-lg relative overflow-hidden
                                 bg-gradient-to-r from-emerald-500 via-emerald-400 to-teal-300
                                 hover:from-emerald-400 hover:to-teal-200
                                 text-zinc-950 text-xs font-extrabold tracking-wider uppercase
                                 shadow-[0_0_24px_rgba(16,185,129,0.45)]
                                 hover:shadow-[0_0_32px_rgba(16,185,129,0.7)]
                                 transition-all flex items-center justify-center gap-2">
                      <Send className="h-4 w-4"/> Send for Approval → Client
                    </button>
                  </div>
                </>
              ) : (
                <div className="grid grid-cols-3 gap-2">
                  <button onClick={()=>decide('rejected')} disabled={busy} className="h-10 rounded bg-rose-500/10 hover:bg-rose-500/20 border border-rose-500/40 text-rose-300 text-xs font-semibold flex items-center justify-center gap-1" data-testid="act-reject"><XCircle className="h-3.5 w-3.5"/>Reject</button>
                  <button onClick={()=>decide('revise')} disabled={busy} className="h-10 rounded bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/40 text-amber-300 text-xs font-semibold flex items-center justify-center gap-1" data-testid="act-revise"><RotateCcw className="h-3.5 w-3.5"/>Revise</button>
                  <button onClick={()=>decide('approved')} disabled={busy} className="h-10 rounded bg-emerald-500 hover:bg-emerald-400 text-zinc-900 text-xs font-bold flex items-center justify-center gap-1" data-testid="act-approve"><CheckCircle2 className="h-3.5 w-3.5"/>Approve</button>
                </div>
              )}
              {msg && <div className="text-[10px] text-zinc-400 text-center pt-1">{msg}</div>}

              {/* 🛡️ EXPRESS OVERRIDE — master_admin only. Visible whenever
                  the submission is at supervisor_review OR pm_review. Style
                  is intentionally aggressive (red, shielded, separated) so
                  it isn't fat-fingered. */}
              {user?.role === 'master_admin' &&
               ['supervisor_review', 'internal_review', 'pm_review'].includes(detail?.submission?.status) && (
                <div className="mt-3 pt-3 border-t border-rose-500/20" data-testid="override-section">
                  <button
                    onClick={() => setOverrideOpen(true)}
                    disabled={busy}
                    data-testid="open-master-override"
                    className="w-full h-11 rounded-sm bg-gradient-to-r from-rose-700 via-red-600 to-rose-700 hover:from-rose-600 hover:to-red-500 text-white text-[11px] font-extrabold tracking-[0.18em] uppercase flex items-center justify-center gap-2 shadow-[0_0_18px_rgba(244,63,94,0.3)] hover:shadow-[0_0_28px_rgba(244,63,94,0.55)] transition-all"
                  >
                    <ShieldCheck className="h-4 w-4" /> Master Admin Override · Send to Client
                  </button>
                  <p className="text-[9px] text-rose-400/80 text-center mt-1.5 leading-relaxed">
                    Use only if Supervisor + Producer are both unavailable. A reason is required and the override is permanently logged. Both reviewers will be emailed.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
        {overrideOpen && (
          <MasterOverrideModal
            onClose={() => setOverrideOpen(false)}
            onConfirm={async (reason) => { await masterOverride(reason); setOverrideOpen(false); }}
            busy={busy}
            submission={detail?.submission || detail}
          />
        )}
      </div>
    );
  }
}

function MasterOverrideModal({ onClose, onConfirm, busy, submission }) {
  const [reason, setReason] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const valid = reason.trim().length >= 10 && confirmText === 'OVERRIDE';
  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" data-testid="master-override-modal" onClick={onClose}>
      <div className="bg-zinc-950 border-2 border-rose-500/60 rounded-lg max-w-lg w-full p-6 shadow-[0_0_60px_rgba(244,63,94,0.35)]" onClick={e => e.stopPropagation()}>
        <div className="flex items-start gap-3 pb-4 border-b border-rose-500/30">
          <div className="shrink-0 w-12 h-12 rounded-full bg-rose-500/20 border border-rose-500/50 flex items-center justify-center">
            <ShieldCheck className="w-6 h-6 text-rose-300" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-extrabold text-white tracking-tight">Master Admin Override</h3>
            <p className="text-[11px] text-rose-300/90 leading-snug mt-1">
              Bypasses Supervisor + Producer gates in one click. Audited permanently.
            </p>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white" data-testid="override-close">
            <XCircle className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-3 py-4 text-xs text-zinc-300">
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-3">
            <p className="text-[10px] text-zinc-500 uppercase tracking-widest mb-1">You're pushing</p>
            <p className="text-white font-semibold">{submission?.title || 'Submission'}</p>
            <p className="text-[10px] text-zinc-500 mt-0.5">
              {submission?.stage} · currently <span className="text-amber-300 font-mono">{submission?.status}</span> →
              going straight to <span className="text-emerald-300 font-mono">client_review</span>
            </p>
          </div>

          <div>
            <label className="text-[10px] text-zinc-400 uppercase tracking-wider">Reason (required, ≥10 chars) *</label>
            <textarea
              value={reason} onChange={e => setReason(e.target.value)}
              rows={3}
              data-testid="override-reason"
              placeholder="e.g. Sanjay is on flight, Pushpa OOO, client meeting at 9pm — pushing animatic round 2"
              className="w-full mt-1 bg-zinc-900 border border-zinc-700 focus:border-rose-500 rounded-sm text-xs text-white p-2 outline-none"
            />
            <p className="text-[9px] text-zinc-600 mt-1">
              {reason.trim().length}/10 minimum · Both Supervisor + Producer will be notified by email with this reason.
            </p>
          </div>

          <div>
            <label className="text-[10px] text-zinc-400 uppercase tracking-wider">Type <span className="font-mono text-rose-300">OVERRIDE</span> to confirm</label>
            <input
              value={confirmText} onChange={e => setConfirmText(e.target.value.toUpperCase())}
              data-testid="override-confirm-input"
              className="w-full mt-1 bg-zinc-900 border border-zinc-700 focus:border-rose-500 rounded-sm text-xs text-white p-2 font-mono uppercase outline-none"
              placeholder="OVERRIDE"
            />
          </div>
        </div>

        <div className="flex items-center gap-2 pt-3 border-t border-zinc-800">
          <button onClick={onClose} disabled={busy} data-testid="override-cancel"
            className="h-10 px-4 text-xs text-zinc-300 hover:text-white border border-zinc-700 hover:bg-zinc-900 rounded-sm">
            Cancel
          </button>
          <button
            onClick={() => onConfirm(reason)}
            disabled={!valid || busy}
            data-testid="override-confirm"
            className="flex-1 h-10 rounded-sm font-extrabold text-xs uppercase tracking-wider transition-all
                       bg-gradient-to-r from-rose-700 via-red-600 to-rose-700
                       hover:from-rose-600 hover:to-red-500
                       disabled:from-zinc-800 disabled:to-zinc-800 disabled:text-zinc-600
                       text-white disabled:cursor-not-allowed
                       shadow-[0_0_18px_rgba(244,63,94,0.3)]
                       hover:shadow-[0_0_28px_rgba(244,63,94,0.55)]"
          >
            {busy ? 'Pushing…' : 'Confirm Override · Send to Client'}
          </button>
        </div>
      </div>
    </div>
  );
}

function InboxCard({ sub, onOpen, canDelete, onDelete, onAuditTrail, historyView, onMoveToPending }) {
  const { api, user } = useAuth();
  const age = fmtAge(sub.submitted_at);
  const firstMedia = sub.media?.[0];
  const [thumbUrl, setThumbUrl] = useState('');

  useEffect(() => {
    if (!firstMedia?.storage_key) return;
    let cancelled = false;
    let blobUrl = '';
    (async () => {
      try {
        const resp = await api.get(`/reviews/media?key=${encodeURIComponent(firstMedia.storage_key)}`, { responseType: 'blob' });
        if (cancelled) return;
        if (resp.data && resp.data.size > 0) {
          blobUrl = URL.createObjectURL(resp.data);
          setThumbUrl(blobUrl);
          return;
        }
      } catch {}
      // Fallback: use the url field directly
      if (!cancelled && firstMedia.url) {
        const directUrl = firstMedia.url.startsWith('http') ? firstMedia.url : `${process.env.REACT_APP_BACKEND_URL}${firstMedia.url.startsWith('/api') ? '' : '/api'}${firstMedia.url}`;
        setThumbUrl(directUrl);
      }
    })();
    return () => { cancelled = true; if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [firstMedia?.storage_key]);

  const isVideo = firstMedia?.type === 'video';
  const isAudio = firstMedia?.type === 'audio';
  return (
    <div className="group relative bg-zinc-900 border border-zinc-800 hover:border-amber-400/50 hover:bg-zinc-900/80 transition-colors rounded-xl overflow-hidden" data-testid={`review-card-${sub.id}`}>
      <button onClick={onOpen} className="block w-full text-left">
        <div className="aspect-video bg-black flex items-center justify-center relative overflow-hidden">
          {isAudio ? (
            <div className="flex flex-col items-center gap-2">
              <div className="w-14 h-14 rounded-full bg-amber-500/20 flex items-center justify-center">
                <Music className="w-6 h-6 text-amber-400" />
              </div>
              <span className="text-[9px] text-zinc-500">Audio</span>
            </div>
          ) : thumbUrl ? (
            isVideo ? (
              <video src={thumbUrl} className="w-full h-full object-cover" muted playsInline preload="metadata" data-testid={`thumb-video-${sub.id}`} />
            ) : (
              <img src={thumbUrl} alt={sub.title} className="w-full h-full object-cover" data-testid={`thumb-img-${sub.id}`} />
            )
          ) : (
            isVideo ? <Film className="h-10 w-10 text-zinc-600 group-hover:text-amber-400"/> : <ImageIcon className="h-10 w-10 text-zinc-600 group-hover:text-amber-400"/>
          )}
          {isVideo && thumbUrl && (
            <span className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <Film className="w-8 h-8 text-white/70 drop-shadow-lg" />
            </span>
          )}
          <span className="absolute top-2 right-2 text-[10px] bg-black/70 text-white px-1.5 py-0.5 rounded">{sub.media?.length || 0} files</span>
          <StagePill stage={sub.stage} className="absolute bottom-2 left-2" />
          {/* Persistent decision badge — once a client approves / requests
              changes / rejects, the decision + timestamp must stay visible
              forever so they can always see what they decided. */}
          {sub.client_decision && (
            <DecisionBadge decision={sub.client_decision} at={sub.client_reviewed_at} />
          )}
        </div>
        <div className="p-3">
          <h3 className="text-sm font-semibold text-white truncate font-mono" title={sub.title}>{sub._display_title || sub.title}</h3>
          {sub._display_title && sub._display_title !== sub.title && (
            <p className="text-[10px] text-zinc-500 truncate mt-0.5" title={sub.title}>original: {sub.title}</p>
          )}
          <div className="flex items-center gap-2 mt-1 text-[11px] text-zinc-500">
            <User className="h-3 w-3"/><span className="text-zinc-200">{displayNameFor(sub, user?.role)}</span>{shouldRenderHandlePill(sub, user?.role) ? <span className="text-cyan-400 font-mono">@{handleFor(sub)}</span> : null}
            <span>·</span>
            <Clock className="h-3 w-3"/>{age}
            <span className="flex-1 text-right">Round {sub.round}</span>
          </div>
          {/* Decision-made timestamp — shown below thumbnail when card represents
              an item the client has already decided on. */}
          {sub.client_decision && sub.client_reviewed_at && (
            <div className="mt-1.5 text-[10px] text-emerald-300/80 flex items-center gap-1.5" data-testid={`decision-ts-${sub.id}`}>
              <CheckCircle2 className="w-3 h-3" />
              <span>
                You {sub.client_decision === 'approved' ? 'approved' : sub.client_decision === 'revise' ? 'requested changes on' : 'rejected'} this on{' '}
                <span className="font-mono">{fmtDate(sub.client_reviewed_at)}</span>
              </span>
            </div>
          )}
          {/* Move to Pending Review button — for approved items */}
          {sub.client_decision === 'approved' && historyView === 'approved' && onMoveToPending && (
            <button onClick={(e) => { e.stopPropagation(); onMoveToPending(sub.id); }}
              data-testid={`revert-pending-${sub.id}`}
              className="mt-2 w-full h-7 bg-amber-500/10 border border-amber-500/30 hover:bg-amber-500/20 text-amber-400 text-[10px] font-semibold rounded flex items-center justify-center gap-1 transition-all">
              Move to Pending Review
            </button>
          )}
          {/* Latest client comment — surfaces Chloë-style feedback
              directly on the Producer/PM inbox card so Pushpa sees the
              note without opening the detail panel. */}
          {sub.latest_client_note && (
            <div className="mt-2 p-2 bg-amber-500/10 border-l-2 border-amber-400 rounded-r text-[11px]"
                 data-testid={`client-note-${sub.id}`}>
              <div className="flex items-center gap-1 mb-0.5">
                <MessageSquare className="w-3 h-3 text-amber-300" />
                <span className="text-[9px] uppercase tracking-wider font-bold text-amber-300">
                  Client note
                  {sub.client_note_count > 1 && <span className="ml-1 text-amber-200/60">· {sub.client_note_count} comments</span>}
                </span>
              </div>
              <p className="text-amber-100/90 line-clamp-3 leading-snug">{sub.latest_client_note}</p>
            </div>
          )}
        </div>
      </button>
      {/* Audit-trail button — shows the chronological journey of this asset
          (every submission round, every decision, every comment, with
          timestamps + actors). Always visible (no hover-gating) so it's
          discoverable. */}
      {onAuditTrail && (
        <button
          onClick={(e) => { e.stopPropagation(); e.preventDefault(); onAuditTrail(); }}
          data-testid={`audit-trail-${sub.id}`}
          title="View full asset journey (every stage, decision, comment)"
          className="absolute top-2 right-2 h-7 px-2 rounded-md bg-violet-500/90 hover:bg-violet-400 text-white text-[10px] font-bold uppercase tracking-wider flex items-center gap-1 z-10 backdrop-blur-sm shadow-md"
        >
          <Activity className="h-3 w-3" /> Activity
        </button>
      )}
      {canDelete && onDelete && (
        <button
          onClick={(e) => { e.stopPropagation(); e.preventDefault(); onDelete(); }}
          data-testid={`delete-submission-${sub.id}`}
          title="Delete this submission (cleans up duplicates)"
          className="absolute top-2 left-2 h-7 w-7 rounded-full bg-black/60 hover:bg-rose-500 text-rose-200 hover:text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10 backdrop-blur-sm"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

function AnnotationRow({ a, idx, mediaUrl, mediaType, onResolve, onDelete, isActive = false, onActivate }) {
  const { user } = useAuth();
  const rowRef = useRef(null);
  const tc = a.timecode_sec != null ? fmtTc(a.timecode_sec) : null;
  const statusPill = a.resolved
    ? { label: 'Resolved',        cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' }
    : { label: 'Changes Requested', cls: 'bg-rose-500/15 text-rose-300 border-rose-500/30' };
  // When this row becomes the active selection (e.g. user clicked the matching
  // pin on the video), scroll it into view + flash a highlight ring.
  useEffect(() => {
    if (isActive && rowRef.current) {
      rowRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [isActive]);
  return (
    <li
      ref={rowRef}
      onClick={() => onActivate?.()}
      className={`px-4 py-3 cursor-pointer transition-colors ${
        isActive
          ? 'bg-amber-500/10 ring-2 ring-amber-400/60 ring-inset'
          : 'hover:bg-zinc-900'
      } ${a.resolved ? 'opacity-70' : ''}`}
      data-testid={`annotation-row-${idx}`}
      data-active={isActive ? 'true' : 'false'}
    >
      <div className="flex items-start gap-2.5">
        <div className={`h-5 w-5 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 mt-0.5 ${a.resolved ? 'bg-emerald-400/20 text-emerald-300' : 'bg-rose-400/20 text-rose-300'}`}>{idx}</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap text-[11px] text-zinc-400">
            <span className="font-semibold text-white">{displayNameFor(a, user?.role)}</span>
            {shouldRenderHandlePill(a, user?.role) ? <span className="font-mono text-cyan-400 text-[10px]">@{handleFor(a)}</span> : null}
            {tc && <span className="font-mono text-amber-300 bg-amber-400/10 border border-amber-400/30 rounded px-1">@ {tc}</span>}
            <span className={`text-[9px] uppercase tracking-wider font-semibold rounded px-1.5 py-0.5 border ${statusPill.cls}`}>{statusPill.label}</span>
            <span className="ml-auto text-[10px] text-zinc-600">{fmtAge(a.created_at)}</span>
          </div>

          {/* Body: thumbnail + text */}
          <div className="flex items-start gap-2.5 mt-1.5">
            {mediaUrl && (
              <AnnotationThumbnail src={mediaUrl} type={mediaType} ann={a} size={{ w: 96, h: 60 }} />
            )}
            <p className="text-xs text-zinc-200 leading-snug whitespace-pre-wrap break-words flex-1 min-w-0 pt-0.5">{a.text}</p>
          </div>

          <div className="flex gap-3 mt-2 text-[10px]">
            {!a.resolved && <button onClick={(e)=>{e.stopPropagation(); onResolve(a.id);}} data-testid={`resolve-ann-${a.id}`} className="text-emerald-400 hover:text-emerald-300">Mark resolved</button>}
            <button onClick={(e)=>{e.stopPropagation(); onDelete(a.id);}} className="text-rose-400 hover:text-rose-300">Delete</button>
          </div>
        </div>
      </div>
    </li>
  );
}

function EmptyState({ mode }) {
  // Founder bug fix Feb 2026: previously hardcoded "client approvals" — now
  // matches whichever gate the viewer is on (supervisor / pm / client / feedback).
  const label = mode === 'supervisor' ? 'supervisor-gate'
               : mode === 'pm'         ? 'producer-gate'
               : mode === 'feedback'   ? 'client feedback'
               : 'client';
  return (
    <div className="bg-zinc-900/50 border border-dashed border-zinc-800 rounded-2xl p-16 text-center">
      <CheckCircle2 className="h-10 w-10 text-zinc-700 mx-auto mb-3"/>
      <h3 className="text-sm font-semibold text-zinc-300">All caught up</h3>
      <p className="text-xs text-zinc-500 mt-1">No {label} approvals pending right now.</p>
    </div>
  );
}

function StagePill({ stage, className = '' }) {
  const COLORS = {
    vo:'#f472b6', storyboard:'#a78bfa', animatic:'#c084fc', layout:'#60a5fa',
    modeling:'#22d3ee', rigging:'#14b8a6', shading:'#2dd4bf', anim:'#f59e0b',
    fx:'#ef4444', light:'#fb923c', comp:'#84cc16', render:'#10b981',
  };
  const c = COLORS[stage] || '#64748b';
  return <span className={`text-[10px] uppercase tracking-wider font-bold rounded px-1.5 py-0.5 ${className}`} style={{color: c, background: c+'22', border:`1px solid ${c}55`}}>{stage}</span>;
}

// Persistent badge that overlays the thumbnail with the client's last decision
// + the timestamp of when they made it. Stays visible forever so the client
// can scan their decisions visually.
function DecisionBadge({ decision, at }) {
  const MAP = {
    approved: { label: 'Approved',          cls: 'bg-emerald-500/95 text-white' },
    revise:   { label: 'Changes Requested', cls: 'bg-rose-500/95 text-white' },
    rejected: { label: 'Rejected',          cls: 'bg-red-600/95 text-white' },
  };
  const m = MAP[decision];
  if (!m) return null;
  const ts = at ? fmtDate(at) : '';
  return (
    <div className={`absolute top-2 left-2 ${m.cls} px-2 py-1 rounded shadow-lg flex items-center gap-1.5`} data-testid={`decision-badge-${decision}`}>
      <span className="text-[10px] font-bold uppercase tracking-wider">{m.label}</span>
      {ts && <span className="text-[9px] font-mono opacity-90">· {ts}</span>}
    </div>
  );
}

function StatusBadge({ status }) {  const MAP = {
    supervisor_review:{ label:'Supervisor Review', color:'text-cyan-300 bg-cyan-500/10 border-cyan-500/40' },
    internal_review:  { label:'Supervisor Review', color:'text-cyan-300 bg-cyan-500/10 border-cyan-500/40' },
    pm_review:        { label:'Producer Review',  color:'text-violet-300 bg-violet-500/10 border-violet-500/40' },
    client_review:    { label:'Client Review',    color:'text-amber-300 bg-amber-500/10 border-amber-500/40' },
    supervisor_revise:{ label:'Revise (supervisor)', color:'text-orange-300 bg-orange-500/10 border-orange-500/40' },
    pm_revise:        { label:'Revise (producer)',   color:'text-orange-300 bg-orange-500/10 border-orange-500/40' },
    internal_revise:  { label:'Revise (internal)', color:'text-orange-300 bg-orange-500/10 border-orange-500/40' },
    client_revise:    { label:'Revise (client)',  color:'text-orange-300 bg-orange-500/10 border-orange-500/40' },
    client_rejected:  { label:'Rejected',         color:'text-rose-300 bg-rose-500/10 border-rose-500/40' },
    final_approved:   { label:'Approved',         color:'text-emerald-300 bg-emerald-500/10 border-emerald-500/40' },
  };
  const m = MAP[status] || { label: status, color:'text-zinc-300 bg-zinc-500/10 border-zinc-500/40' };
  return <span className={`text-[10px] uppercase tracking-wider font-bold rounded px-2 py-1 border ${m.color}`}>{m.label}</span>;
}

function fmtAge(iso) {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms/60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m/60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h/24);
  return `${d}d ago`;
}

// Compact human date used on persistent decision badges. e.g. "27 Apr 4:15pm"
function fmtDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const day = d.getDate();
    const mon = d.toLocaleString('en', { month: 'short' });
    const h12 = d.getHours() % 12 || 12;
    const mm = String(d.getMinutes()).padStart(2, '0');
    const ap = d.getHours() < 12 ? 'am' : 'pm';
    return `${day} ${mon} ${h12}:${mm}${ap}`;
  } catch { return ''; }
}
function fmtTc(t) { const m = Math.floor(t/60).toString().padStart(2,'0'); const s = Math.floor(t%60).toString().padStart(2,'0'); return `${m}:${s}`; }
